import { useState, useRef, useEffect } from 'react'
import { Mic, Radio, Tv, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import type { SeriesType, SeriesWithCount } from '@/types/series'
import { useLabels, singular } from '@/lib/terminology'

interface SeriesCardProps {
  series: SeriesWithCount
  onClick?: () => void
  onEdit?: () => void
  onDelete?: () => void
}

const SERIES_TYPE_ICONS: Record<SeriesType, React.ElementType> = {
  podcast: Mic,
  channel: Radio,
  show: Tv,
}

const SERIES_TYPE_COLORS: Record<SeriesType, string> = {
  podcast: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  channel: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  show: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
}

export function SeriesCard({ series, onClick, onEdit, onDelete }: SeriesCardProps) {
  const labels = useLabels()
  const Icon = SERIES_TYPE_ICONS[series.series_type]
  const typeColor = SERIES_TYPE_COLORS[series.series_type]
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isMenuOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen])

  return (
    <div
      onClick={onClick}
      className="group relative p-5 rounded-xl border bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 cursor-pointer transition-all hover:shadow-md"
    >
      {/* Cover image or placeholder */}
      <div className="relative h-32 mb-4 rounded-lg overflow-hidden bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-700">
        {series.cover_image_url ? (
          <img
            src={series.cover_image_url}
            alt={series.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-12 h-12 text-zinc-400 dark:text-zinc-500" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 truncate flex-1">
            {series.name}
          </h3>

          {/* Actions dropdown */}
          {(onEdit || onDelete) && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsMenuOpen((prev) => !prev)
                }}
                aria-label={`${singular(labels.collections)} actions`}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              >
                <MoreVertical className="w-4 h-4 text-zinc-500" />
              </button>
              {isMenuOpen && (
                <div role="menu" className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 z-10 min-w-32">
                  {onEdit && (
                    <button
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsMenuOpen(false)
                        onEdit()
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation()
                        setIsMenuOpen(false)
                        onDelete()
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-zinc-100 dark:hover:bg-zinc-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {series.description && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2">
            {series.description}
          </p>
        )}

        <div className="flex items-center gap-3 pt-2">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${typeColor}`}
          >
            <Icon className="w-3 h-3" />
            {series.series_type.charAt(0).toUpperCase() + series.series_type.slice(1)}
          </span>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            {series.episode_count} episode{series.episode_count !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  )
}
